// FamilyOfficeDashboard.js - Family Office Investor Dashboard
// URL-path-based tabs, zinc/white/monospace Tailwind design, inline demo data

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Home, Users, Target, DollarSign, ArrowRightLeft, TrendingUp, Wallet,
  Briefcase, Globe, Building2, MessageSquare, Settings, Search, RefreshCw,
  CheckCircle, Clock, AlertTriangle, Eye, FileText, BarChart3, Calendar,
  Percent, CreditCard, Shield, LineChart, ExternalLink
} from 'lucide-react';
import { getStatusStyle } from '../../config/statusColors';
import { useAuth } from '../../context/AuthContext';
import ProjectionsPage from '../projections/ProjectionsPage';
import PortfolioPage from '../portfolio/PortfolioPage';
import YieldDifferentialExplorer from '../common/YieldDifferentialExplorer';
import RatesBoard from '../rates/RatesBoard';
import ComplianceTab from '../compliance/ComplianceTab';
import FxOverlayTab from '../portfolio/FxOverlayTab';
import LiquidityTab from '../liquidity/LiquidityTab';
import usePortfolioData from '../../hooks/usePortfolioData';
import { validateTicket } from '../../lib/dealing';
import { csdGateway } from '../../services/csdGateway';
import { validateTransition, TRANSACTION_STATES } from '../../lib/transactionLifecycle';
import { UPCOMING_AUCTIONS, AUCTION_RULES_KENYA } from '../../data/dealingMockData';

// ============================================
// DEMO / FALLBACK DATA
// ============================================
const demoData = {
  organization: {
    name: 'Meridian Family Office',
    type: 'Family Office',
    jurisdiction: 'Singapore',
    entities: [
      { id: 'ENT-001', name: 'Meridian Holdings Ltd', type: 'Holding Company', jurisdiction: 'Singapore', status: 'active' },
      { id: 'ENT-002', name: 'Meridian Capital (BVI)', type: 'SPV', jurisdiction: 'British Virgin Islands', status: 'active' },
      { id: 'ENT-003', name: 'Meridian Trust', type: 'Trust', jurisdiction: 'Jersey', status: 'active' },
      { id: 'ENT-004', name: 'MFO Investments Ltd', type: 'Investment Vehicle', jurisdiction: 'Cayman Islands', status: 'active' },
    ],
    teamMembers: [
      { id: 'USR-01', name: 'Victoria Chen', role: 'Principal', email: 'v.chen@meridianfo.com', status: 'active', permissions: 'Full Access', lastLogin: '2026-09-12T09:15:00Z' },
      { id: 'USR-02', name: 'Marcus Li', role: 'CIO', email: 'm.li@meridianfo.com', status: 'active', permissions: 'Trading + Admin', lastLogin: '2026-09-12T08:42:00Z' },
      { id: 'USR-03', name: 'Sarah Tan', role: 'Portfolio Manager', email: 's.tan@meridianfo.com', status: 'active', permissions: 'Trading', lastLogin: '2026-09-11T17:30:00Z' },
      { id: 'USR-04', name: 'David Ng', role: 'Analyst', email: 'd.ng@meridianfo.com', status: 'active', permissions: 'View Only', lastLogin: '2026-09-12T10:05:00Z' },
      { id: 'USR-05', name: 'Rachel Koh', role: 'Compliance Officer', email: 'r.koh@meridianfo.com', status: 'active', permissions: 'Compliance + View', lastLogin: '2026-09-11T14:20:00Z' },
    ],
    approvalThreshold: 5000000,
    bankerPreferences: [
      { bank: 'Standard Chartered', priority: 1, relationship: 'Primary' },
      { bank: 'HSBC', priority: 2, relationship: 'Secondary' },
      { bank: 'Citi', priority: 3, relationship: 'Tertiary' },
    ],
  },
  markets: {
    summary: { marketsAvailable: 6, avgYield: 14.2, nextAuction: '2026-09-18' },
    rates: [
      { market: 'Kenya', tenor: '91-day', rate: 16.25, lastAuction: '2026-09-05' },
      { market: 'Kenya', tenor: '182-day', rate: 16.80, lastAuction: '2026-09-05' },
      { market: 'Kenya', tenor: '364-day', rate: 17.10, lastAuction: '2026-09-05' },
      { market: 'Uganda', tenor: '91-day', rate: 14.50, lastAuction: '2026-09-03' },
      { market: 'Uganda', tenor: '182-day', rate: 15.20, lastAuction: '2026-09-03' },
      { market: 'Uganda', tenor: '364-day', rate: 15.75, lastAuction: '2026-09-03' },
      { market: 'Tanzania', tenor: '91-day', rate: 8.90, lastAuction: '2026-09-04' },
      { market: 'Tanzania', tenor: '182-day', rate: 9.40, lastAuction: '2026-09-04' },
      { market: 'Tanzania', tenor: '364-day', rate: 10.10, lastAuction: '2026-09-04' },
      { market: 'Nigeria', tenor: '91-day', rate: 18.50, lastAuction: '2026-09-02' },
      { market: 'Nigeria', tenor: '182-day', rate: 19.20, lastAuction: '2026-09-02' },
      { market: 'Nigeria', tenor: '364-day', rate: 19.80, lastAuction: '2026-09-02' },
      { market: 'Ghana', tenor: '91-day', rate: 28.50, lastAuction: '2026-09-06' },
      { market: 'Ghana', tenor: '182-day', rate: 29.10, lastAuction: '2026-09-06' },
      { market: 'Rwanda', tenor: '91-day', rate: 10.20, lastAuction: '2026-09-01' },
      { market: 'Rwanda', tenor: '182-day', rate: 10.80, lastAuction: '2026-09-01' },
    ],
    auctionCalendar: [
      { market: 'Kenya', date: '2026-09-18', instrument: '91-day T-Bill', status: 'upcoming' },
      { market: 'Kenya', date: '2026-09-18', instrument: '182-day T-Bill', status: 'upcoming' },
      { market: 'Uganda', date: '2026-09-17', instrument: '91-day T-Bill', status: 'upcoming' },
      { market: 'Tanzania', date: '2026-09-19', instrument: '182-day T-Bill', status: 'upcoming' },
      { market: 'Nigeria', date: '2026-09-22', instrument: '364-day T-Bill', status: 'upcoming' },
      { market: 'Ghana', date: '2026-09-20', instrument: '91-day T-Bill', status: 'upcoming' },
      { market: 'Rwanda', date: '2026-09-24', instrument: '182-day T-Bill', status: 'upcoming' },
    ],
  },
  dealing: {
    auctions: [
      { id: 'AUC-7001', market: 'Kenya', instrument: 'T-Bill', tenor: '91-day', yield: 16.25, volume: 25000000000, status: 'open' },
      { id: 'AUC-7002', market: 'Kenya', instrument: 'T-Bill', tenor: '182-day', yield: 16.80, volume: 15000000000, status: 'open' },
      { id: 'AUC-7003', market: 'Uganda', instrument: 'T-Bill', tenor: '91-day', yield: 14.50, volume: 500000000000, status: 'open' },
      { id: 'AUC-7004', market: 'Nigeria', instrument: 'T-Bill', tenor: '364-day', yield: 19.80, volume: 300000000000, status: 'results_published' },
      { id: 'AUC-7005', market: 'Tanzania', instrument: 'T-Bill', tenor: '182-day', yield: 9.40, volume: 100000000000, status: 'open' },
      { id: 'AUC-7006', market: 'Ghana', instrument: 'T-Bill', tenor: '91-day', yield: 28.50, volume: 50000000000, status: 'closed' },
    ],
    bidHistory: [
      { id: 'BID-101', auction: 'AUC-7001', market: 'Kenya', tenor: '91-day', amount: 2000000, rate: 16.30, status: 'submitted', date: '2026-09-12T08:30:00Z' },
      { id: 'BID-102', auction: 'AUC-7002', market: 'Kenya', tenor: '182-day', amount: 1500000, rate: 16.85, status: 'allocated', date: '2026-09-11T10:00:00Z' },
      { id: 'BID-103', auction: 'AUC-7003', market: 'Uganda', tenor: '91-day', amount: 3000000, rate: 14.55, status: 'submitted', date: '2026-09-12T09:15:00Z' },
      { id: 'BID-104', auction: 'AUC-7004', market: 'Nigeria', tenor: '364-day', amount: 5000000, rate: 19.90, status: 'allocated', date: '2026-09-10T14:00:00Z' },
      { id: 'BID-105', auction: 'AUC-7006', market: 'Ghana', tenor: '91-day', amount: 1000000, rate: 28.55, status: 'unsuccessful', date: '2026-09-09T11:00:00Z' },
    ],
  },
  fx: {
    activeRfqs: [
      { id: 'FX-501', currencyPair: 'USD/KES', amount: 2000000, direction: 'Buy', banksInvited: 4, quotesReceived: 3, bestRate: 129.42, status: 'open' },
      { id: 'FX-502', currencyPair: 'USD/UGX', amount: 3000000, direction: 'Buy', banksInvited: 3, quotesReceived: 2, bestRate: 3718.50, status: 'open' },
      { id: 'FX-503', currencyPair: 'EUR/KES', amount: 1500000, direction: 'Buy', banksInvited: 5, quotesReceived: 5, bestRate: 141.85, status: 'completed' },
      { id: 'FX-504', currencyPair: 'GBP/TZS', amount: 800000, direction: 'Sell', banksInvited: 3, quotesReceived: 0, bestRate: null, status: 'pending' },
      { id: 'FX-505', currencyPair: 'USD/NGN', amount: 5000000, direction: 'Buy', banksInvited: 4, quotesReceived: 4, bestRate: 1520.30, status: 'fx_rate_locked' },
      { id: 'FX-506', currencyPair: 'USD/KES', amount: 1200000, direction: 'Sell', banksInvited: 3, quotesReceived: 1, bestRate: 129.55, status: 'open' },
    ],
  },
  forwards: {
    forwardCurve: [
      { tenor: '1M', usdKes: 130.10, usdUgx: 3735.00, eurKes: 142.80 },
      { tenor: '3M', usdKes: 131.45, usdUgx: 3768.00, eurKes: 144.20 },
      { tenor: '6M', usdKes: 133.20, usdUgx: 3815.00, eurKes: 146.10 },
      { tenor: '9M', usdKes: 135.00, usdUgx: 3862.00, eurKes: 148.00 },
      { tenor: '12M', usdKes: 136.85, usdUgx: 3910.00, eurKes: 149.90 },
    ],
    activeContracts: [
      { id: 'FWD-801', pair: 'USD/KES', notional: 5000000, strikeRate: 131.20, tenor: '3M', maturity: '2026-12-15', mtm: 85200, status: 'active' },
      { id: 'FWD-802', pair: 'USD/UGX', notional: 3000000, strikeRate: 3755.00, tenor: '6M', maturity: '2027-03-12', mtm: -22400, status: 'active' },
      { id: 'FWD-803', pair: 'EUR/KES', notional: 2000000, strikeRate: 143.50, tenor: '3M', maturity: '2026-12-10', mtm: 41000, status: 'active' },
      { id: 'FWD-804', pair: 'USD/KES', notional: 8000000, strikeRate: 134.80, tenor: '9M', maturity: '2027-06-12', mtm: 192500, status: 'active' },
      { id: 'FWD-805', pair: 'USD/KES', notional: 2500000, strikeRate: 129.50, tenor: '1M', maturity: '2026-10-12', mtm: 15800, status: 'maturing' },
    ],
  },
  settlement: {
    pendingSettlements: [
      {
        id: 'STL-901', instrument: 'Kenya 91-day T-Bill', market: 'Kenya', tenor: '91-day',
        notional: 2000000, fxRate: 129.45, forwardsPrice: 131.20, tax: 3200, fees: 1500,
        total: 2004700, status: 'pending_settlement', requiresApproval: false,
      },
      {
        id: 'STL-902', instrument: 'Uganda 91-day T-Bill', market: 'Uganda', tenor: '91-day',
        notional: 3000000, fxRate: 3718.50, forwardsPrice: 3755.00, tax: 4800, fees: 2250,
        total: 3007050, status: 'pending_settlement', requiresApproval: false,
      },
      {
        id: 'STL-903', instrument: 'Nigeria 364-day T-Bill', market: 'Nigeria', tenor: '364-day',
        notional: 5000000, fxRate: 1520.30, forwardsPrice: null, tax: 8000, fees: 3750,
        total: 5011750, status: 'pending_approval', requiresApproval: true,
      },
      {
        id: 'STL-904', instrument: 'Kenya 182-day T-Bill', market: 'Kenya', tenor: '182-day',
        notional: 1500000, fxRate: 129.42, forwardsPrice: 131.45, tax: 2400, fees: 1125,
        total: 1503525, status: 'confirmed', requiresApproval: false,
      },
    ],
  },
  portfolio: {
    summary: { totalAum: 42500000, weightedYield: 16.8, avgMaturityDays: 145 },
    holdings: [
      { id: 'H-001', instrument: 'Kenya 91-day T-Bill', market: 'Kenya', notional: 8000000, entryDate: '2026-07-15', maturity: '2026-10-15', yield: 16.25, currentValue: 8320000, pnl: 320000 },
      { id: 'H-002', instrument: 'Kenya 182-day T-Bill', market: 'Kenya', notional: 5000000, entryDate: '2026-06-01', maturity: '2026-12-01', yield: 16.80, currentValue: 5280000, pnl: 280000 },
      { id: 'H-003', instrument: 'Uganda 91-day T-Bill', market: 'Uganda', notional: 6000000, entryDate: '2026-08-01', maturity: '2026-11-01', yield: 14.50, currentValue: 6174000, pnl: 174000 },
      { id: 'H-004', instrument: 'Nigeria 364-day T-Bill', market: 'Nigeria', notional: 10000000, entryDate: '2026-03-15', maturity: '2027-03-15', yield: 19.80, currentValue: 10990000, pnl: 990000 },
      { id: 'H-005', instrument: 'Tanzania 182-day T-Bill', market: 'Tanzania', notional: 4000000, entryDate: '2026-07-01', maturity: '2026-12-30', yield: 9.40, currentValue: 4094000, pnl: 94000 },
      { id: 'H-006', instrument: 'Ghana 91-day T-Bill', market: 'Ghana', notional: 3500000, entryDate: '2026-08-20', maturity: '2026-11-20', yield: 28.50, currentValue: 3749250, pnl: 249250 },
      { id: 'H-007', instrument: 'Kenya 364-day T-Bill', market: 'Kenya', notional: 4000000, entryDate: '2026-01-10', maturity: '2027-01-10', yield: 17.10, currentValue: 4456000, pnl: 456000 },
      { id: 'H-008', instrument: 'Rwanda 182-day T-Bill', market: 'Rwanda', notional: 2000000, entryDate: '2026-08-10', maturity: '2027-02-10', yield: 10.80, currentValue: 2036000, pnl: 36000 },
    ],
    maturitySchedule: [
      { month: 'Oct 2026', amount: 14000000 },
      { month: 'Nov 2026', amount: 9500000 },
      { month: 'Dec 2026', amount: 9280000 },
      { month: 'Jan 2027', amount: 4000000 },
      { month: 'Feb 2027', amount: 2000000 },
      { month: 'Mar 2027', amount: 10000000 },
    ],
  },
  secondary: {
    listings: [
      { id: 'SEC-301', instrument: 'Kenya 91-day T-Bill', market: 'Kenya', notional: 2000000, askYield: 15.80, listedDate: '2026-09-10', status: 'active' },
      { id: 'SEC-302', instrument: 'Ghana 91-day T-Bill', market: 'Ghana', notional: 1000000, askYield: 27.90, listedDate: '2026-09-08', status: 'active' },
    ],
    offers: [
      { id: 'OFR-401', listing: 'SEC-301', bidYield: 15.90, bidAmount: 2000000, bidder: 'Counterparty A', date: '2026-09-11', status: 'pending' },
      { id: 'OFR-402', listing: 'SEC-301', bidYield: 16.00, bidAmount: 1500000, bidder: 'Counterparty B', date: '2026-09-11', status: 'pending' },
      { id: 'OFR-403', listing: 'SEC-302', bidYield: 28.10, bidAmount: 1000000, bidder: 'Counterparty C', date: '2026-09-12', status: 'pending' },
    ],
  },
  profile: {
    kycStatus: [
      { bank: 'Standard Chartered', kycStatus: 'verified', documentsStatus: 'completed', cdsCardStatus: 'cds_card_complete', lastUpdated: '2026-08-15' },
      { bank: 'HSBC', kycStatus: 'verified', documentsStatus: 'completed', cdsCardStatus: 'cds_card_complete', lastUpdated: '2026-08-20' },
      { bank: 'Citi', kycStatus: 'pending_kyc', documentsStatus: 'incomplete', cdsCardStatus: 'cds_card_pending', lastUpdated: '2026-09-01' },
      { bank: 'Barclays Africa', kycStatus: 'kyc_maker_review', documentsStatus: 'submitted', cdsCardStatus: 'cds_card_pending', lastUpdated: '2026-09-05' },
    ],
    consents: [
      { id: 'CON-01', description: 'Share KYC data with partner banks', granted: true, grantedDate: '2026-06-15' },
      { id: 'CON-02', description: 'Receive auction notifications via email', granted: true, grantedDate: '2026-06-15' },
      { id: 'CON-03', description: 'Allow automated settlement processing', granted: true, grantedDate: '2026-07-01' },
      { id: 'CON-04', description: 'Share portfolio data for credit assessment', granted: false, grantedDate: null },
      { id: 'CON-05', description: 'Participate in secondary market anonymously', granted: true, grantedDate: '2026-08-01' },
    ],
  },
  messages: [
    { id: 'MSG-001', timestamp: '2026-09-12T11:30:00Z', category: 'auction', content: 'Kenya 91-day T-Bill auction results published. Your bid BID-102 was allocated at 16.85%.' },
    { id: 'MSG-002', timestamp: '2026-09-12T10:15:00Z', category: 'settlement', content: 'Settlement STL-904 confirmed. Kenya 182-day T-Bill funds received.' },
    { id: 'MSG-003', timestamp: '2026-09-12T09:45:00Z', category: 'fx', content: 'Foreign Exchange RFQ FX-505 rate locked at 1520.30 USD/NGN. Settlement window opens in 2 hours.' },
    { id: 'MSG-004', timestamp: '2026-09-11T16:00:00Z', category: 'compliance', content: 'KYC review with Citi requires additional documentation. Please upload updated financial statements.' },
    { id: 'MSG-005', timestamp: '2026-09-11T14:30:00Z', category: 'portfolio', content: 'Forward contract FWD-805 (USD/KES) maturing on 2026-10-12. Action required: elect rollover or realise.' },
    { id: 'MSG-006', timestamp: '2026-09-11T11:00:00Z', category: 'secondary', content: 'New bid received on SEC-301 (Kenya 91-day T-Bill) at 15.90% yield for $2M notional.' },
    { id: 'MSG-007', timestamp: '2026-09-10T17:00:00Z', category: 'auction', content: 'Upcoming auction reminder: Kenya T-Bills (91-day, 182-day) on 2026-09-18.' },
    { id: 'MSG-008', timestamp: '2026-09-10T09:00:00Z', category: 'system', content: 'Platform maintenance scheduled for 2026-09-14 02:00-04:00 UTC. Trading will be paused.' },
    { id: 'MSG-009', timestamp: '2026-09-09T15:20:00Z', category: 'fx', content: 'Foreign Exchange RFQ FX-503 completed. EUR/KES locked at 141.85. Best of 5 quotes.' },
    { id: 'MSG-010', timestamp: '2026-09-09T10:00:00Z', category: 'compliance', content: 'Annual KYC refresh completed with Standard Chartered and HSBC.' },
  ],
};

// ============================================
// FORMAT HELPERS
// ============================================
const formatCurrency = (val) => {
  if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
  return `$${val?.toLocaleString() || 0}`;
};

const formatNumber = (val) => val?.toLocaleString() || '0';

const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '\u2014';
const formatDateTime = (d) => {
  if (!d) return '\u2014';
  const dt = new Date(d);
  return `${dt.toLocaleDateString()} ${dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const formatPnl = (val) => {
  const prefix = val >= 0 ? '+' : '';
  if (Math.abs(val) >= 1000000) return `${prefix}$${(val / 1000000).toFixed(2)}M`;
  if (Math.abs(val) >= 1000) return `${prefix}$${(val / 1000).toFixed(0)}K`;
  return `${prefix}$${val?.toLocaleString() || 0}`;
};

// ============================================
// STATUS BADGE HELPER
// ============================================
const getStatusBadge = (status) => {
  const style = getStatusStyle(status);
  return {
    className: 'inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border rounded-none',
    style: { ...style, borderColor: style.color },
  };
};

// ============================================
// STAT CARD COMPONENT
// ============================================
const StatCard = ({ label, value, icon: Icon, trend }) => (
  <div className="bg-white border border-zinc-200 rounded-none p-6">
    <div className="flex items-center justify-between mb-2">
      <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">{label}</span>
      <Icon className="w-4 h-4 text-zinc-400" />
    </div>
    <p className="text-2xl font-bold text-black">{value}</p>
    {trend && <p className="text-xs text-zinc-500 mt-1">{trend}</p>}
  </div>
);

// ============================================
// URL PATH TO TAB MAPPING
// ============================================
const getTabFromPath = (pathname) => {
  // Pillar paths
  if (pathname.includes('/admin/yield')) return 'markets';
  if (pathname.includes('/admin/rates')) return 'fx';
  if (pathname.includes('/admin/compliance')) return 'compliance';
  if (pathname.includes('/admin/hedging')) return 'forwards_market';
  if (pathname.includes('/admin/liquidity')) return 'secondary_market';
  if (pathname.includes('/admin/execution')) return 'dealing_room';
  // Legacy paths (backward compat)
  if (pathname.includes('/admin/markets')) return 'markets';
  if (pathname.includes('/admin/dealing')) return 'dealing_room';
  if (pathname.includes('/admin/fx')) return 'fx';
  if (pathname.includes('/admin/forwards')) return 'forwards_market';
  if (pathname.includes('/admin/settlement')) return 'settlement';
  if (pathname.includes('/admin/portfolio')) return 'portfolio';
  if (pathname.includes('/admin/secondary')) return 'secondary_market';
  if (pathname.includes('/admin/projections')) return 'projections';
  if (pathname.includes('/admin/profile')) return 'profile';
  if (pathname.includes('/admin/messages')) return 'messages';
  return 'administration';
};

// ============================================
// CATEGORY ICON MAP
// ============================================
const categoryIcons = {
  auction: Target,
  settlement: CheckCircle,
  fx: ArrowRightLeft,
  compliance: Shield,
  portfolio: Briefcase,
  secondary: BarChart3,
  system: Settings,
};

// ============================================
// MAIN FAMILY OFFICE DASHBOARD
// ============================================
const FamilyOfficeDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { positions: portfolioPositions, fxHedges } = usePortfolioData();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Form states
  const [bidAmount, setBidAmount] = useState('');
  const [bidRate, setBidRate] = useState('');
  const [selectedAuction, setSelectedAuction] = useState('');
  const [fxPair, setFxPair] = useState('USD/KES');
  const [fxAmount, setFxAmount] = useState('');
  const [fxDirection, setFxDirection] = useState('Buy');
  const [fwdPair, setFwdPair] = useState('USD/KES');
  const [fwdAmount, setFwdAmount] = useState('');
  const [fwdTenor, setFwdTenor] = useState('3M');
  const [saleInstrument, setSaleInstrument] = useState('');
  const [saleNotional, setSaleNotional] = useState('');
  const [saleAskYield, setSaleAskYield] = useState('');
  const [settlementAgreed, setSettlementAgreed] = useState({});

  // Bid submission state
  const [bidSubmitting, setBidSubmitting] = useState(false);
  const [bidError, setBidError] = useState(null);
  const [bidSuccess, setBidSuccess] = useState(null);

  // Settlement lifecycle state
  const [settlementStatuses, setSettlementStatuses] = useState({});
  const [settleSubmitting, setSettleSubmitting] = useState(null);

  // Demo lifecycle driver — walks bid through states after submission
  const [demoLifecycle, setDemoLifecycle] = useState(null);

  // Data states
  const [org] = useState(demoData.organization);
  const [marketsData] = useState(demoData.markets);
  const [dealingData] = useState(demoData.dealing);
  const [fxData] = useState(demoData.fx);
  const [forwardsData] = useState(demoData.forwards);
  const [settlementData] = useState(demoData.settlement);
  const [portfolioData] = useState(demoData.portfolio);
  const [secondaryData] = useState(demoData.secondary);
  const [profileData] = useState(demoData.profile);
  const [messages] = useState(demoData.messages);

  // Determine active tab from URL path
  const [activeTab, setActiveTab] = useState(() => getTabFromPath(location.pathname));

  // Update tab when URL changes
  useEffect(() => {
    setActiveTab(getTabFromPath(location.pathname));
  }, [location.pathname]);

  // Simulate initial load
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 400);
    return () => clearTimeout(timer);
  }, []);

  // Demo lifecycle driver: walks bid through states with timed delays
  const DEMO_LIFECYCLE_STEPS = [
    { state: 'bid_placed', label: 'Bid Placed', delay: 0 },
    { state: 'submitted_to_csd', label: 'Submitted to CSD', delay: 2000 },
    { state: 'bid_allocated', label: 'Bid Allocated', delay: 3500 },
    { state: 'settlement_pending', label: 'Settlement Pending', delay: 5000 },
    { state: 'fx_rfq_sent', label: 'FX RFQ Sent', delay: 6500 },
    { state: 'fx_rate_locked', label: 'FX Rate Locked', delay: 8000 },
    { state: 'bank_instruction_confirmed', label: 'Bank Confirmed', delay: 9500 },
    { state: 'csd_confirmed', label: 'CSD Confirmed', delay: 11000 },
    { state: 'portfolio_active', label: 'Portfolio Active', delay: 12500 },
  ];

  useEffect(() => {
    if (!demoLifecycle) return;
    const timers = DEMO_LIFECYCLE_STEPS.slice(1).map((step, i) =>
      setTimeout(() => {
        setDemoLifecycle(prev => prev ? { ...prev, currentStep: i + 1, state: step.state } : null);
      }, step.delay)
    );
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoLifecycle?.csdRef]);

  // TABS CONFIGURATION
  const tabs = [
    { id: 'administration', label: 'Overview', path: '/investor/admin', icon: Home },
    { id: 'markets', label: 'Yield Discovery', path: '/investor/admin/yield', icon: Target },
    { id: 'fx', label: 'Rates & FX', path: '/investor/admin/rates', icon: ArrowRightLeft },
    { id: 'compliance', label: 'Compliance', path: '/investor/admin/compliance', icon: Shield },
    { id: 'forwards_market', label: 'Hedging', path: '/investor/admin/hedging', icon: TrendingUp },
    { id: 'dealing_room', label: 'Execution', path: '/investor/admin/execution', icon: DollarSign },
    { id: 'settlement', label: 'Settlement', path: '/investor/admin/settlement', icon: CheckCircle },
    { id: 'portfolio', label: 'Portfolio', path: '/investor/admin/portfolio', icon: Wallet },
    { id: 'secondary_market', label: 'Liquidity', path: '/investor/admin/liquidity', icon: BarChart3 },
    { id: 'projections', label: 'Projections', path: '/investor/admin/projections', icon: LineChart },
    { id: 'profile', label: 'Profile', path: '/investor/admin/profile', icon: Building2 },
    { id: 'messages', label: 'Messages', path: '/investor/admin/messages', icon: MessageSquare },
  ];

  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 600);
  };

  // ─── Submit Bid Handler ──────────────────────────────────────────
  const handleSubmitBid = async () => {
    setBidError(null);
    setBidSuccess(null);

    if (!selectedAuction) { setBidError('Please select an auction.'); return; }
    if (!bidAmount || Number(bidAmount) <= 0) { setBidError('Please enter a valid bid amount.'); return; }

    const auction = UPCOMING_AUCTIONS.find(a => a.id === selectedAuction);
    if (!auction) { setBidError('Auction not found.'); return; }

    const amount = Number(bidAmount);
    const rate = bidRate ? Number(bidRate) : null;
    const isCompetitive = rate && rate > 0;

    const ticket = {
      id: `TKT-NEW-${Date.now()}`,
      entityId: 'ENT-001',
      auctionId: selectedAuction,
      status: 'draft',
      tranches: [{ type: isCompetitive ? 'competitive' : 'non_competitive', amount, yield: rate }],
      fundingRoute: 'RTGS',
      createdAt: new Date().toISOString(),
    };

    const validation = validateTicket(ticket, auction);
    if (!validation.valid) {
      setBidError(validation.errors.join('; '));
      return;
    }

    setBidSubmitting(true);
    try {
      const result = await csdGateway.KE.submitBid(ticket);
      if (result.success) {
        setBidSuccess(`Bid submitted — CSD Ref: ${result.csdRef}`);
        setSelectedAuction('');
        setBidAmount('');
        setBidRate('');
        // Start demo lifecycle progression
        setDemoLifecycle({ csdRef: result.csdRef, currentStep: 0, state: 'bid_placed', startedAt: Date.now() });
      } else {
        setBidError('CSD submission failed. Please retry.');
      }
    } catch (err) {
      setBidError(err.message || 'Unexpected error during bid submission.');
    } finally {
      setBidSubmitting(false);
    }
  };

  // ─── Settle Handler ──────────────────────────────────────────────
  const handleSettle = async (stl) => {
    setSettleSubmitting(stl.id);
    const currentState = TRANSACTION_STATES.SETTLEMENT_PENDING;
    const targetState = TRANSACTION_STATES.FX_RFQ_SENT;
    const role = 'investor_family_office';

    const check = validateTransition(currentState, targetState, role);
    if (!check.valid) {
      alert(`Settlement transition blocked: ${check.reason}`);
      setSettleSubmitting(null);
      return;
    }

    // Simulate async settlement progression
    await new Promise(r => setTimeout(r, 300));
    setSettlementStatuses(prev => ({ ...prev, [stl.id]: 'confirmed' }));
    setSettleSubmitting(null);
  };

  // LOADING STATE
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-zinc-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-zinc-600 mt-4">Loading family office dashboard...</p>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="p-6 bg-zinc-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-zinc-600 to-zinc-800 rounded-none p-6 text-white mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-6 h-6" />
              <span className="text-zinc-200 text-sm font-medium">FAMILY OFFICE</span>
            </div>
            <h1 className="text-2xl font-bold">{user?.tenantName || org.name}</h1>
            <p className="text-zinc-100 mt-1">
              {org.entities.length} entities &bull; {org.teamMembers.length} team members &bull; {org.jurisdiction}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-zinc-200">Total AUM</p>
            <p className="text-3xl font-bold">{formatCurrency(portfolioData.summary.totalAum)}</p>
            <p className="text-sm text-zinc-200 mt-1">Weighted Yield: {portfolioData.summary.weightedYield}%</p>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex gap-2 mb-6 bg-white rounded-none p-1 shadow-sm overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => navigate(tab.path)}
            className={`flex items-center gap-2 px-4 py-2 rounded-none font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.id ? 'bg-zinc-600 text-white' : 'text-zinc-600 hover:bg-zinc-100'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
        <div className="flex-1" />
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 text-zinc-600 hover:bg-zinc-100 rounded-none"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* ============================================ */}
      {/* ADMINISTRATION TAB                          */}
      {/* ============================================ */}
      {activeTab === 'administration' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Entities" value={org.entities.length} icon={Building2} trend="Org structure" />
            <StatCard label="Team Members" value={org.teamMembers.length} icon={Users} trend="Active users" />
            <StatCard label="Approval Threshold" value={formatCurrency(org.approvalThreshold)} icon={Shield} trend="Multi-sig required above" />
            <StatCard label="Partner Banks" value={org.bankerPreferences.length} icon={Briefcase} trend="Assigned relationships" />
          </div>

          {/* Entity Tree */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Entity Structure</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 border border-zinc-200 rounded-none bg-zinc-50">
                <Building2 className="w-5 h-5 text-zinc-600" />
                <div className="flex-1">
                  <p className="font-bold text-sm text-black">{org.name}</p>
                  <p className="text-[10px] font-mono uppercase text-zinc-400">{org.type} &mdash; {org.jurisdiction}</p>
                </div>
                <span {...getStatusBadge('active')}>active</span>
              </div>
              {org.entities.map(entity => (
                <div key={entity.id} className="flex items-center gap-3 p-3 border border-zinc-100 rounded-none ml-8">
                  <div className="w-px h-6 bg-zinc-300 -ml-4 mr-2" />
                  <Globe className="w-4 h-4 text-zinc-400" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-black">{entity.name}</p>
                    <p className="text-[10px] font-mono uppercase text-zinc-400">{entity.type} &mdash; {entity.jurisdiction}</p>
                  </div>
                  <span className="font-mono text-[10px] text-zinc-400 uppercase">{entity.id}</span>
                  <span {...getStatusBadge(entity.status)}>{entity.status}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Team Members */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">Users & Roles</h3>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  type="text"
                  placeholder="Search team..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-4 py-2 border border-zinc-200 rounded-none text-sm focus:outline-none focus:border-zinc-400"
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Name</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Role</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Email</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Permissions</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Last Login</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {org.teamMembers
                    .filter(m => !searchQuery || m.name.toLowerCase().includes(searchQuery.toLowerCase()) || m.role.toLowerCase().includes(searchQuery.toLowerCase()))
                    .map(member => (
                    <tr key={member.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-3 px-4">
                        <div className="font-medium text-black">{member.name}</div>
                        <span className="font-mono text-[9px] text-zinc-400">{member.id}</span>
                      </td>
                      <td className="py-3 px-4 font-mono text-xs">{member.role}</td>
                      <td className="py-3 px-4 text-xs text-zinc-500">{member.email}</td>
                      <td className="py-3 px-4 text-xs text-zinc-500">{member.permissions}</td>
                      <td className="py-3 px-4 text-xs text-zinc-500">{formatDateTime(member.lastLogin)}</td>
                      <td className="py-3 px-4"><span {...getStatusBadge(member.status)}>{member.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Approval Threshold & Banker Preferences */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-zinc-200 rounded-none p-6">
              <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Approval Threshold</h3>
              <div className="flex items-center gap-4 p-4 border border-zinc-100 rounded-none">
                <AlertTriangle className="w-5 h-5 text-zinc-500" />
                <div>
                  <p className="text-sm text-zinc-600">Transactions above <span className="font-bold text-black">{formatCurrency(org.approvalThreshold)}</span> require multi-signature approval.</p>
                  <p className="text-[10px] font-mono uppercase text-zinc-400 mt-1">Minimum 2 of 3 signatories required</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-zinc-200 rounded-none p-6">
              <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Banker Assignment Preferences</h3>
              <div className="space-y-2">
                {org.bankerPreferences.map((bp, i) => (
                  <div key={i} className="flex items-center justify-between p-3 border border-zinc-100 rounded-none">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-[10px] text-zinc-400 w-4">{bp.priority}.</span>
                      <span className="text-sm font-medium text-black">{bp.bank}</span>
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-400">{bp.relationship}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Trading Facilities */}
          <div>
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Trading Facilities</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div
                onClick={() => navigate('/investor/admin/fx')}
                className="bg-white border border-zinc-200 rounded-none p-6 hover:shadow-md transition-shadow cursor-pointer group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 bg-zinc-100 rounded-none flex items-center justify-center group-hover:bg-zinc-200 transition-colors">
                    <ArrowRightLeft className="w-5 h-5 text-zinc-600" />
                  </div>
                  <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-[10px] font-mono uppercase tracking-wider rounded">Use anytime</span>
                </div>
                <h4 className="font-semibold text-sm text-black mb-1">Foreign Exchange</h4>
                <p className="text-xs text-zinc-500">Request competitive spot FX quotes from multiple partner banks</p>
              </div>
              <div
                onClick={() => navigate('/investor/admin/forwards')}
                className="bg-white border border-zinc-200 rounded-none p-6 hover:shadow-md transition-shadow cursor-pointer group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 bg-zinc-100 rounded-none flex items-center justify-center group-hover:bg-zinc-200 transition-colors">
                    <TrendingUp className="w-5 h-5 text-zinc-600" />
                  </div>
                  <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-[10px] font-mono uppercase tracking-wider rounded">Use anytime</span>
                </div>
                <h4 className="font-semibold text-sm text-black mb-1">Forwards Contracts</h4>
                <p className="text-xs text-zinc-500">Buy forward contracts competitively from multiple partner banks</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* MARKETS TAB                                  */}
      {/* ============================================ */}
      {activeTab === 'markets' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <StatCard label="Markets Available" value={marketsData.summary.marketsAvailable} icon={Globe} trend="African frontier markets" />
            <StatCard label="Avg Yield" value={`${marketsData.summary.avgYield}%`} icon={Percent} trend="Across all tenors" />
            <StatCard label="Next Auction" value={formatDate(marketsData.summary.nextAuction)} icon={Calendar} trend="Uganda 91-day T-Bill" />
          </div>

          {/* Yield Differential Explorer */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">Yield Differential Explorer</h3>
              <a
                href="/simulator"
                className="flex items-center gap-1.5 px-3 py-1.5 border border-zinc-200 rounded-none text-[10px] font-mono uppercase tracking-[0.12em] text-zinc-600 hover:bg-zinc-50 transition-colors"
              >
                <ExternalLink className="w-3 h-3" />
                Open in Simulator
              </a>
            </div>
            <YieldDifferentialExplorer />
          </div>

          {/* T-Bill Rates */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">T-Bill Rates by Market</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Market</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Tenor</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Rate</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Last Auction</th>
                  </tr>
                </thead>
                <tbody>
                  {marketsData.rates.map((r, i) => (
                    <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-3 px-4 font-medium text-black">{r.market}</td>
                      <td className="py-3 px-4 font-mono text-xs">{r.tenor}</td>
                      <td className="py-3 px-4 text-right font-mono text-xs font-bold">{r.rate}%</td>
                      <td className="py-3 px-4 text-xs text-zinc-500">{formatDate(r.lastAuction)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Auction Calendar */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Upcoming Auction Calendar</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Date</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Market</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Instrument</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {marketsData.auctionCalendar.map((a, i) => (
                    <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-3 px-4 font-mono text-xs font-bold">{formatDate(a.date)}</td>
                      <td className="py-3 px-4 text-sm text-black">{a.market}</td>
                      <td className="py-3 px-4 text-sm text-zinc-600">{a.instrument}</td>
                      <td className="py-3 px-4"><span {...getStatusBadge(a.status)}>{a.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* DEALING ROOM TAB                             */}
      {/* ============================================ */}
      {activeTab === 'dealing_room' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Open Auctions" value={dealingData.auctions.filter(a => a.status === 'open').length} icon={Target} trend="Active for bidding" />
            <StatCard label="Bids Submitted" value={dealingData.bidHistory.filter(b => b.status === 'submitted').length} icon={FileText} trend="Awaiting results" />
            <StatCard label="Bids Allocated" value={dealingData.bidHistory.filter(b => b.status === 'allocated').length} icon={CheckCircle} trend="Successfully allocated" />
            <StatCard label="Total Bid Volume" value={formatCurrency(dealingData.bidHistory.reduce((s, b) => s + b.amount, 0))} icon={DollarSign} trend="All bids" />
          </div>

          {/* Current Auctions */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Current Auctions</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Auction ID</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Market</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Instrument</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Tenor</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Yield</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Volume</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {dealingData.auctions.map(auction => (
                    <tr key={auction.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-3 px-4 font-mono text-xs">{auction.id}</td>
                      <td className="py-3 px-4 font-medium text-black">{auction.market}</td>
                      <td className="py-3 px-4 text-zinc-600">{auction.instrument}</td>
                      <td className="py-3 px-4 font-mono text-xs">{auction.tenor}</td>
                      <td className="py-3 px-4 text-right font-mono text-xs font-bold">{auction.yield}%</td>
                      <td className="py-3 px-4 text-right font-mono text-xs">{formatNumber(auction.volume)}</td>
                      <td className="py-3 px-4"><span {...getStatusBadge(auction.status)}>{auction.status.replace('_', ' ')}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bid Construction Form */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Bid Construction</h3>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">Auction</label>
                <select
                  value={selectedAuction}
                  onChange={(e) => setSelectedAuction(e.target.value)}
                  className="w-full border border-zinc-200 rounded-none px-3 py-2 text-sm focus:outline-none focus:border-zinc-400"
                >
                  <option value="">Select auction...</option>
                  {dealingData.auctions.filter(a => a.status === 'open').map(a => (
                    <option key={a.id} value={a.id}>{a.id} - {a.market} {a.tenor}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">Amount (USD)</label>
                <input
                  type="number"
                  value={bidAmount}
                  onChange={(e) => setBidAmount(e.target.value)}
                  placeholder="2,000,000"
                  className="w-full border border-zinc-200 rounded-none px-3 py-2 text-sm focus:outline-none focus:border-zinc-400"
                />
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">Rate (%)</label>
                <input
                  type="number"
                  step="0.01"
                  value={bidRate}
                  onChange={(e) => setBidRate(e.target.value)}
                  placeholder="16.30"
                  className="w-full border border-zinc-200 rounded-none px-3 py-2 text-sm focus:outline-none focus:border-zinc-400"
                />
              </div>
              <div>
                <button
                  onClick={handleSubmitBid}
                  disabled={bidSubmitting}
                  className={`w-full px-4 py-2 font-mono text-xs uppercase tracking-wider transition-colors rounded-none ${
                    bidSubmitting
                      ? 'bg-zinc-400 text-zinc-200 cursor-wait'
                      : 'bg-zinc-700 text-white hover:bg-zinc-800'
                  }`}
                >
                  {bidSubmitting ? 'Submitting...' : 'Submit Bid'}
                </button>
              </div>
            </div>
            {bidError && (
              <div className="mt-3 flex items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-none">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> {bidError}
              </div>
            )}
            {bidSuccess && (
              <div className="mt-3 flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2 rounded-none">
                <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" /> {bidSuccess}
              </div>
            )}
          </div>

          {/* Demo Lifecycle Tracker */}
          {demoLifecycle && (
            <div className="bg-white border border-zinc-200 rounded-none p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">Transaction Lifecycle</h3>
                <span className="font-mono text-[10px] text-zinc-400">{demoLifecycle.csdRef}</span>
              </div>
              <div className="flex items-center gap-1 overflow-x-auto pb-2">
                {DEMO_LIFECYCLE_STEPS.map((step, i) => {
                  const isCurrent = demoLifecycle.currentStep === i;
                  const isComplete = demoLifecycle.currentStep > i;
                  return (
                    <React.Fragment key={step.state}>
                      <div className={`flex items-center gap-1.5 px-2.5 py-1.5 border rounded-none text-[9px] font-mono uppercase whitespace-nowrap ${
                        isComplete
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                          : isCurrent
                            ? 'bg-zinc-700 border-zinc-700 text-white'
                            : 'bg-white border-zinc-200 text-zinc-400'
                      }`}>
                        {isComplete ? <CheckCircle className="w-3 h-3" /> : isCurrent ? <Clock className="w-3 h-3 animate-pulse" /> : null}
                        {step.label}
                      </div>
                      {i < DEMO_LIFECYCLE_STEPS.length - 1 && (
                        <div className={`w-4 h-px flex-shrink-0 ${isComplete ? 'bg-emerald-300' : 'bg-zinc-200'}`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}

          {/* Bid History */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Bid History</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Bid ID</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Auction</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Market</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Tenor</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Amount</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Rate</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Status</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {dealingData.bidHistory.map(bid => (
                    <tr key={bid.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-3 px-4 font-mono text-xs">{bid.id}</td>
                      <td className="py-3 px-4 font-mono text-xs text-zinc-500">{bid.auction}</td>
                      <td className="py-3 px-4 text-black">{bid.market}</td>
                      <td className="py-3 px-4 font-mono text-xs">{bid.tenor}</td>
                      <td className="py-3 px-4 text-right font-mono text-xs">{formatCurrency(bid.amount)}</td>
                      <td className="py-3 px-4 text-right font-mono text-xs font-bold">{bid.rate}%</td>
                      <td className="py-3 px-4"><span {...getStatusBadge(bid.status)}>{bid.status}</span></td>
                      <td className="py-3 px-4 text-xs text-zinc-500">{formatDateTime(bid.date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* FX TAB                                       */}
      {/* ============================================ */}
      {activeTab === 'fx' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Active RFQs" value={fxData.activeRfqs.filter(r => r.status === 'open').length} icon={ArrowRightLeft} trend="Awaiting quotes" />
            <StatCard label="Quotes Received" value={fxData.activeRfqs.reduce((s, r) => s + r.quotesReceived, 0)} icon={FileText} trend="Total across RFQs" />
            <StatCard label="Rates Locked" value={fxData.activeRfqs.filter(r => r.status === 'fx_rate_locked').length} icon={CheckCircle} trend="Ready for settlement" />
            <StatCard label="Total Foreign Exchange Volume" value={formatCurrency(fxData.activeRfqs.reduce((s, r) => s + r.amount, 0))} icon={DollarSign} trend="All RFQs" />
          </div>

          {/* Rates Board */}
          <RatesBoard />

          {/* Post FX RFQ */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Post Foreign Exchange RFQ</h3>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">Currency Pair</label>
                <select
                  value={fxPair}
                  onChange={(e) => setFxPair(e.target.value)}
                  className="w-full border border-zinc-200 rounded-none px-3 py-2 text-sm focus:outline-none focus:border-zinc-400"
                >
                  <option value="USD/KES">USD/KES</option>
                  <option value="USD/UGX">USD/UGX</option>
                  <option value="USD/TZS">USD/TZS</option>
                  <option value="USD/NGN">USD/NGN</option>
                  <option value="EUR/KES">EUR/KES</option>
                  <option value="GBP/KES">GBP/KES</option>
                  <option value="GBP/TZS">GBP/TZS</option>
                </select>
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">Amount (USD)</label>
                <input
                  type="number"
                  value={fxAmount}
                  onChange={(e) => setFxAmount(e.target.value)}
                  placeholder="1,000,000"
                  className="w-full border border-zinc-200 rounded-none px-3 py-2 text-sm focus:outline-none focus:border-zinc-400"
                />
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">Direction</label>
                <select
                  value={fxDirection}
                  onChange={(e) => setFxDirection(e.target.value)}
                  className="w-full border border-zinc-200 rounded-none px-3 py-2 text-sm focus:outline-none focus:border-zinc-400"
                >
                  <option value="Buy">Buy</option>
                  <option value="Sell">Sell</option>
                </select>
              </div>
              <div>
                <button className="w-full px-4 py-2 bg-zinc-700 text-white font-mono text-xs uppercase tracking-wider hover:bg-zinc-800 transition-colors rounded-none">
                  Post RFQ
                </button>
              </div>
            </div>
          </div>

          {/* Active RFQs */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Active Foreign Exchange RFQs</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">RFQ ID</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Currency Pair</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Amount</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Direction</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Banks Invited</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Quotes Received</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Best Rate</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Status</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {fxData.activeRfqs.map(rfq => (
                    <tr key={rfq.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-3 px-4 font-mono text-xs">{rfq.id}</td>
                      <td className="py-3 px-4 font-mono text-xs font-bold">{rfq.currencyPair}</td>
                      <td className="py-3 px-4 text-right font-mono text-xs">{formatCurrency(rfq.amount)}</td>
                      <td className="py-3 px-4 text-xs">{rfq.direction}</td>
                      <td className="py-3 px-4 text-right font-mono text-xs">{rfq.banksInvited}</td>
                      <td className="py-3 px-4 text-right font-mono text-xs">{rfq.quotesReceived}</td>
                      <td className="py-3 px-4 text-right font-mono text-xs font-bold">{rfq.bestRate || '\u2014'}</td>
                      <td className="py-3 px-4"><span {...getStatusBadge(rfq.status)}>{rfq.status.replace(/_/g, ' ')}</span></td>
                      <td className="py-3 px-4">
                        {rfq.status === 'open' && rfq.quotesReceived > 0 && (
                          <button className="px-2 py-1 border border-zinc-200 rounded-none text-[10px] font-mono uppercase hover:bg-zinc-50 transition-colors">
                            Choose Rate
                          </button>
                        )}
                        {rfq.status !== 'open' && (
                          <button className="flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded-none text-[10px] font-mono uppercase hover:bg-zinc-50 transition-colors">
                            <Eye className="w-3 h-3" /> View
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* COMPLIANCE TAB (pillar 3 placeholder)        */}
      {/* ============================================ */}
      {activeTab === 'compliance' && (
        <ComplianceTab role="investor_family_office" />
      )}

      {/* ============================================ */}
      {/* FORWARDS MARKET TAB                          */}
      {/* ============================================ */}
      {activeTab === 'forwards_market' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Active Forwards" value={forwardsData.activeContracts.filter(c => c.status === 'active').length} icon={TrendingUp} trend="Open contracts" />
            <StatCard label="Total Notional" value={formatCurrency(forwardsData.activeContracts.reduce((s, c) => s + c.notional, 0))} icon={DollarSign} trend="Forward contracts" />
            <StatCard label="Net MTM" value={formatPnl(forwardsData.activeContracts.reduce((s, c) => s + c.mtm, 0))} icon={BarChart3} trend="Mark-to-market P&L" />
            <StatCard label="Maturing Soon" value={forwardsData.activeContracts.filter(c => c.status === 'maturing').length} icon={Clock} trend="Action required" />
          </div>

          {/* Divestment Rate RFQ Form */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Post Divestment Rate RFQ</h3>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">Currency Pair</label>
                <select
                  value={fwdPair}
                  onChange={(e) => setFwdPair(e.target.value)}
                  className="w-full border border-zinc-200 rounded-none px-3 py-2 text-sm focus:outline-none focus:border-zinc-400"
                >
                  <option value="USD/KES">USD/KES</option>
                  <option value="USD/UGX">USD/UGX</option>
                  <option value="EUR/KES">EUR/KES</option>
                  <option value="GBP/KES">GBP/KES</option>
                </select>
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">Notional (USD)</label>
                <input
                  type="number"
                  value={fwdAmount}
                  onChange={(e) => setFwdAmount(e.target.value)}
                  placeholder="5,000,000"
                  className="w-full border border-zinc-200 rounded-none px-3 py-2 text-sm focus:outline-none focus:border-zinc-400"
                />
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">Tenor</label>
                <select
                  value={fwdTenor}
                  onChange={(e) => setFwdTenor(e.target.value)}
                  className="w-full border border-zinc-200 rounded-none px-3 py-2 text-sm focus:outline-none focus:border-zinc-400"
                >
                  <option value="1M">1 Month</option>
                  <option value="3M">3 Months</option>
                  <option value="6M">6 Months</option>
                  <option value="9M">9 Months</option>
                  <option value="12M">12 Months</option>
                </select>
              </div>
              <div>
                <button className="w-full px-4 py-2 bg-zinc-700 text-white font-mono text-xs uppercase tracking-wider hover:bg-zinc-800 transition-colors rounded-none">
                  Post RFQ
                </button>
              </div>
            </div>
          </div>

          {/* Forward Curve */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Forward Curve Rates</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Tenor</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">USD/KES</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">USD/UGX</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">EUR/KES</th>
                  </tr>
                </thead>
                <tbody>
                  {forwardsData.forwardCurve.map((fc, i) => (
                    <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-3 px-4 font-mono text-xs font-bold">{fc.tenor}</td>
                      <td className="py-3 px-4 text-right font-mono text-xs">{fc.usdKes.toFixed(2)}</td>
                      <td className="py-3 px-4 text-right font-mono text-xs">{fc.usdUgx.toFixed(2)}</td>
                      <td className="py-3 px-4 text-right font-mono text-xs">{fc.eurKes.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Active Forward Contracts */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Active Forward Contracts</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Contract ID</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Pair</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Notional</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Strike Rate</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Tenor</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Maturity</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">MTM</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {forwardsData.activeContracts.map(contract => (
                    <tr key={contract.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-3 px-4 font-mono text-xs">{contract.id}</td>
                      <td className="py-3 px-4 font-mono text-xs font-bold">{contract.pair}</td>
                      <td className="py-3 px-4 text-right font-mono text-xs">{formatCurrency(contract.notional)}</td>
                      <td className="py-3 px-4 text-right font-mono text-xs">{contract.strikeRate.toFixed(2)}</td>
                      <td className="py-3 px-4 font-mono text-xs">{contract.tenor}</td>
                      <td className="py-3 px-4 text-xs text-zinc-500">{formatDate(contract.maturity)}</td>
                      <td className={`py-3 px-4 text-right font-mono text-xs font-bold ${contract.mtm >= 0 ? 'text-black' : 'text-red-600'}`}>
                        {formatPnl(contract.mtm)}
                      </td>
                      <td className="py-3 px-4"><span {...getStatusBadge(contract.status)}>{contract.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Hedge Coverage KPI */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-4 h-4 text-zinc-400" />
              <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">Hedge Coverage</h3>
            </div>
            <p className="font-mono text-2xl font-bold text-black">
              {(() => {
                const totalNotional = forwardsData.activeContracts.reduce((s, c) => s + c.notional, 0);
                const totalExposure = totalNotional * 1.4; // estimated total local-ccy exposure
                return totalExposure > 0 ? `${((totalNotional / totalExposure) * 100).toFixed(0)}%` : '\u2014';
              })()}
            </p>
            <p className="font-mono text-[10px] text-zinc-400 mt-0.5">hedged notional / total local-currency exposure</p>
          </div>

          {/* FX Overlay */}
          <FxOverlayTab positions={portfolioPositions} role="investor_family_office" fxHedges={fxHedges} />
        </div>
      )}

      {/* ============================================ */}
      {/* SETTLEMENT TAB                               */}
      {/* ============================================ */}
      {activeTab === 'settlement' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Pending Settlements" value={settlementData.pendingSettlements.filter(s => s.status === 'pending_settlement').length} icon={Clock} trend="Awaiting confirmation" />
            <StatCard label="Pending Approval" value={settlementData.pendingSettlements.filter(s => s.requiresApproval).length} icon={Shield} trend="Above threshold" />
            <StatCard label="Confirmed" value={settlementData.pendingSettlements.filter(s => s.status === 'confirmed').length} icon={CheckCircle} trend="Settled successfully" />
            <StatCard label="Total Settling" value={formatCurrency(settlementData.pendingSettlements.reduce((s, t) => s + t.total, 0))} icon={DollarSign} trend="Gross settlement value" />
          </div>

          {/* Settlement Details */}
          <div className="space-y-4">
            {settlementData.pendingSettlements.map(stl => (
              <div key={stl.id} className="bg-white border border-zinc-200 rounded-none p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">{stl.id}</h3>
                    <span {...getStatusBadge(settlementStatuses[stl.id] || stl.status)}>{(settlementStatuses[stl.id] || stl.status).replace(/_/g, ' ')}</span>
                    {stl.requiresApproval && !(settlementStatuses[stl.id]) && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border border-zinc-400 text-zinc-600 rounded-none">
                        <Shield className="w-3 h-3" /> Approval Required
                      </span>
                    )}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <tbody>
                      <tr className="border-b border-zinc-100">
                        <td className="py-2 px-4 font-mono text-[10px] uppercase text-zinc-500 w-40">Instrument</td>
                        <td className="py-2 px-4 font-medium text-black">{stl.instrument}</td>
                      </tr>
                      <tr className="border-b border-zinc-100">
                        <td className="py-2 px-4 font-mono text-[10px] uppercase text-zinc-500">Market</td>
                        <td className="py-2 px-4">{stl.market}</td>
                      </tr>
                      <tr className="border-b border-zinc-100">
                        <td className="py-2 px-4 font-mono text-[10px] uppercase text-zinc-500">Tenor</td>
                        <td className="py-2 px-4 font-mono text-xs">{stl.tenor}</td>
                      </tr>
                      <tr className="border-b border-zinc-100">
                        <td className="py-2 px-4 font-mono text-[10px] uppercase text-zinc-500">Notional</td>
                        <td className="py-2 px-4 font-mono text-xs">{formatCurrency(stl.notional)}</td>
                      </tr>
                      <tr className="border-b border-zinc-100">
                        <td className="py-2 px-4 font-mono text-[10px] uppercase text-zinc-500">Foreign Exchange Rate</td>
                        <td className="py-2 px-4 font-mono text-xs">{stl.fxRate}</td>
                      </tr>
                      <tr className="border-b border-zinc-100">
                        <td className="py-2 px-4 font-mono text-[10px] uppercase text-zinc-500">Forwards Price</td>
                        <td className="py-2 px-4 font-mono text-xs">{stl.forwardsPrice || '\u2014'}</td>
                      </tr>
                      <tr className="border-b border-zinc-100">
                        <td className="py-2 px-4 font-mono text-[10px] uppercase text-zinc-500">Tax</td>
                        <td className="py-2 px-4 font-mono text-xs">{formatCurrency(stl.tax)}</td>
                      </tr>
                      <tr className="border-b border-zinc-100">
                        <td className="py-2 px-4 font-mono text-[10px] uppercase text-zinc-500">Fees</td>
                        <td className="py-2 px-4 font-mono text-xs">{formatCurrency(stl.fees)}</td>
                      </tr>
                      <tr className="border-b border-zinc-200">
                        <td className="py-2 px-4 font-mono text-[10px] uppercase text-zinc-500 font-bold">Total</td>
                        <td className="py-2 px-4 font-mono text-xs font-bold text-black">{formatCurrency(stl.total)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                {(settlementStatuses[stl.id] || stl.status) !== 'confirmed' && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-zinc-100">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settlementAgreed[stl.id] || false}
                        onChange={(e) => setSettlementAgreed(prev => ({ ...prev, [stl.id]: e.target.checked }))}
                        className="w-4 h-4 border-zinc-300 rounded-none"
                      />
                      <span className="text-sm text-zinc-600">I agree to this transaction</span>
                    </label>
                    <button
                      onClick={() => handleSettle(stl)}
                      disabled={!settlementAgreed[stl.id] || settleSubmitting === stl.id}
                      className={`px-4 py-2 font-mono text-xs uppercase tracking-wider rounded-none transition-colors ${
                        settlementAgreed[stl.id] && settleSubmitting !== stl.id
                          ? 'bg-zinc-700 text-white hover:bg-zinc-800'
                          : 'bg-zinc-200 text-zinc-400 cursor-not-allowed'
                      }`}
                    >
                      {settleSubmitting === stl.id ? 'Processing...' : stl.requiresApproval ? 'Submit for Approval' : 'Settle'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* PORTFOLIO TAB (PortfolioPage with 7 sub-tabs)*/}
      {/* ============================================ */}
      {activeTab === 'portfolio' && (
        <PortfolioPage role="investor_family_office" />
      )}

      {/* ============================================ */}
      {/* SECONDARY MARKET TAB                         */}
      {/* ============================================ */}
      {activeTab === 'secondary_market' && (
        <LiquidityTab role="investor_family_office" />
      )}

      {/* ============================================ */}
      {/* PROJECTIONS TAB                              */}
      {/* ============================================ */}
      {activeTab === 'projections' && (
        <ProjectionsPage role="investor_family_office" />
      )}

      {/* ============================================ */}
      {/* PROFILE TAB                                  */}
      {/* ============================================ */}
      {activeTab === 'profile' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Banks Onboarded" value={profileData.kycStatus.filter(k => k.kycStatus === 'verified').length} icon={Building2} trend="KYC verified" />
            <StatCard label="Pending KYC" value={profileData.kycStatus.filter(k => k.kycStatus !== 'verified').length} icon={Clock} trend="In progress" />
            <StatCard label="CDS Cards Complete" value={profileData.kycStatus.filter(k => k.cdsCardStatus === 'cds_card_complete').length} icon={CreditCard} trend="Ready for dealing" />
            <StatCard label="Consents Granted" value={profileData.consents.filter(c => c.granted).length} icon={Shield} trend={`of ${profileData.consents.length} total`} />
          </div>

          {/* KYC Status per Bank */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">KYC Status per Bank</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {profileData.kycStatus.map((bank, i) => (
                <div key={i} className="border border-zinc-200 rounded-none p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-zinc-500" />
                      <h4 className="font-bold text-black">{bank.bank}</h4>
                    </div>
                    <span className="text-[10px] font-mono text-zinc-400">Updated {formatDate(bank.lastUpdated)}</span>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[10px] uppercase text-zinc-500">KYC Status</span>
                      <span {...getStatusBadge(bank.kycStatus)}>{bank.kycStatus.replace(/_/g, ' ')}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[10px] uppercase text-zinc-500">Documents</span>
                      <span {...getStatusBadge(bank.documentsStatus)}>{bank.documentsStatus}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[10px] uppercase text-zinc-500">CDS Card</span>
                      <span {...getStatusBadge(bank.cdsCardStatus)}>{bank.cdsCardStatus.replace(/_/g, ' ')}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Consents */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Consents</h3>
            <div className="space-y-3">
              {profileData.consents.map(consent => (
                <div key={consent.id} className="flex items-center justify-between p-4 border border-zinc-100 rounded-none">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${consent.granted ? 'bg-zinc-700' : 'bg-zinc-300'}`} />
                    <div>
                      <p className="text-sm text-black">{consent.description}</p>
                      {consent.grantedDate && (
                        <p className="text-[10px] font-mono text-zinc-400">Granted {formatDate(consent.grantedDate)}</p>
                      )}
                    </div>
                  </div>
                  <span {...getStatusBadge(consent.granted ? 'active' : 'draft')}>
                    {consent.granted ? 'Granted' : 'Not Granted'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* MESSAGES TAB                                 */}
      {/* ============================================ */}
      {activeTab === 'messages' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Total Messages" value={messages.length} icon={MessageSquare} trend="All notifications" />
            <StatCard label="Auction Alerts" value={messages.filter(m => m.category === 'auction').length} icon={Target} trend="Auction lifecycle" />
            <StatCard label="Settlement Alerts" value={messages.filter(m => m.category === 'settlement').length} icon={CheckCircle} trend="Settlement updates" />
            <StatCard label="Action Required" value={messages.filter(m => m.category === 'compliance' || m.category === 'portfolio').length} icon={AlertTriangle} trend="Needs attention" />
          </div>

          {/* Messages List */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">Notifications & Messages</h3>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  type="text"
                  placeholder="Search messages..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-4 py-2 border border-zinc-200 rounded-none text-sm focus:outline-none focus:border-zinc-400"
                />
              </div>
            </div>
            <div className="space-y-2">
              {messages
                .filter(m => !searchQuery || m.content.toLowerCase().includes(searchQuery.toLowerCase()) || m.category.toLowerCase().includes(searchQuery.toLowerCase()))
                .map(msg => {
                  const CategoryIcon = categoryIcons[msg.category] || MessageSquare;
                  return (
                    <div key={msg.id} className="flex items-start gap-4 p-4 border border-zinc-100 rounded-none hover:bg-zinc-50 transition-colors">
                      <div className="p-2 bg-zinc-100 rounded-none mt-0.5">
                        <CategoryIcon className="w-4 h-4 text-zinc-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span {...getStatusBadge(msg.category === 'compliance' ? 'pending' : 'active')}>{msg.category}</span>
                          <span className="text-[10px] font-mono text-zinc-400">{msg.id}</span>
                        </div>
                        <p className="text-sm text-black">{msg.content}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[10px] font-mono text-zinc-400">{formatDateTime(msg.timestamp)}</p>
                      </div>
                    </div>
                  );
                })}
            </div>
            {messages.length === 0 && (
              <div className="text-center py-8 text-zinc-400">
                <MessageSquare className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm">No messages at this time.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FamilyOfficeDashboard;
